import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { QueryClientProvider, hydrate } from '@tanstack/react-query';
import type { DehydratedState } from '@tanstack/react-query';
import { loadPageRoutes } from './app/routes';
import { queryClient } from './app/queryClient';
import './styles/tokens.css';
import './styles/research.css';

async function start() {
  const container = document.getElementById('root');
  if (!container) throw new Error('#root is missing from index.html');
  const snapshot = document.getElementById('slad-page-state');
  const state = snapshot?.textContent
    ? (JSON.parse(snapshot.textContent) as { path: string; state: DehydratedState })
    : undefined;
  const url = window.location.pathname.replace(/\/$/, '') || '/';
  // Filtered pages and unbuilt deep links use the interactive fallback, not mismatched HTML.
  const canHydrate = state?.path === url && !window.location.search;
  if (state) hydrate(queryClient, state.state);
  const router = createBrowserRouter(await loadPageRoutes(window.location.href));
  const app = (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>
  );
  if (canHydrate) hydrateRoot(container, app);
  else createRoot(container).render(app);
}

void start().catch(() => {
  const container = document.getElementById('root');
  if (!container) return;
  createRoot(container).render(
    <main className="sedlex-research-page" role="alert">
      <h1>This page could not finish loading.</h1>
      <p>Check your connection and try again.</p>
      <button
        className="sedlex-btn sedlex-btn--primary"
        onClick={() => window.location.reload()}
      >
        Retry page
      </button>
      <a href="/corpus">Browse the corpus</a>
    </main>,
  );
});
